import React, { useEffect, useState } from "react";
import ImageCropperPanelView from "presentational/upload/ImageCropperPanelView";
import LoadingModalView from "presentational/common/modals/LoadingModalView";
import ImageLoader from "lib/imageLoader";
import TXT from "presentational/common/multinational/TXT";
import imageCropper from "lib/imageCropper";
import { blobToDataURL } from "blob-util";
import { withStore } from "store/storeHandler";
import config from "lib/fr-config";
import PropTypes from "prop-types";

const getImageRatio = type => {
  switch (type) {
    case "profile":
      return config.profileImage.RATIO;
    case "item":
      return config.itemImage.RATIO;
    case "outfit":
      return config.outfitImage.RATIO;
    default:
      console.log("error");
      console.error("error");
      break;
  }
};
const getMinImgWidth = type => {
  switch (type) {
    case "profile":
      return config.profileImage.MINIMUM_WIDTH;
    case "item":
      return config.itemImage.MINIMUM_WIDTH;
    case "outfit":
      return config.outfitImage.MINIMUM_WIDTH;
    default:
      console.log("error");
      console.error("error");
      break;
  }
};
const getMinImgHeight = type => {
  return getMinImgWidth(type) / getImageRatio(type);
};
const getMaxImgWidth = type => {
  switch (type) {
    case "profile":
      return config.profileImage.MAXIMUM_WIDTH;
    case "item":
      return config.itemImage.MAXIMUM_WIDTH;
    case "outfit":
      return config.outfitImage.MAXIMUM_WIDTH;
    default:
      console.log("error");
      console.error("error");
      break;
  }
};
const getMaxImgHeight = type => {
  return getMaxImgWidth(type) / getImageRatio(type);
};

ImageCropperPanel.propTypes = {
  imageType: PropTypes.oneOf(["profile", "item", "outfit"]).isRequired,
  alreadyLoadedImageData: PropTypes.object,
  internalCropData: PropTypes.object,
  resultImageQuality: PropTypes.number,
  onImageCrop: PropTypes.func.isRequired,
  onCancelClick: PropTypes.func.isRequired,
  onNextClick: PropTypes.func.isRequired,
  hasAddImageButtonClickedAtLeastOnce: PropTypes.bool.isRequired,
  onAddImageBtnClick: PropTypes.func.isRequired
};

function ImageCropperPanel(props) {
  const {
    imageType,
    alreadyLoadedImageData,
    internalCropData,
    resultImageQuality,
    onImageCrop,
    onCancelClick,
    onNextClick,
    hasAddImageButtonClickedAtLeastOnce,
    onAddImageBtnClick,
    NoticeModalActions,
    AppErrorActions
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [cropData, setCropData] = useState({
    originalImageElem: internalCropData && internalCropData.originalImageElem,
    originalImageDataURL:
      internalCropData && internalCropData.originalImageDataURL,
    cropPixels: {
      x: internalCropData && internalCropData.cropPixels.x,
      y: internalCropData && internalCropData.cropPixels.y,
      width: internalCropData && internalCropData.cropPixels.width,
      height: internalCropData && internalCropData.cropPixels.height
    }
  });
  const { originalImageElem, originalImageDataURL, cropPixels } = cropData;

  useEffect(() => {
    initAlreadyLoadedImage();
  }, []);

  const initAlreadyLoadedImage = () => {
    if (!alreadyLoadedImageData) return null;

    const { originalImageElem, originalImageDataURL } = alreadyLoadedImageData;
    setCropData({
      originalImageElem,
      originalImageDataURL,
      cropPixels: { x: null, y: null, width: null, height: null }
    });
  };

  const getMaxImgSize = ({ width, height }) => {
    const maxImgWidth = getMaxImgWidth(imageType);
    const maxImgHeight = getMaxImgHeight(imageType);
    const canvasWidth = maxImgWidth < width ? maxImgWidth : width;
    const canvasHeight = maxImgHeight < height ? maxImgHeight : height;
    return { canvasWidth, canvasHeight };
  };

  const isSizeOfImageEnough = imgElem => {
    const { width, height } = imgElem;
    if (
      width < getMinImgWidth(imageType) ||
      height < getMinImgHeight(imageType)
    ) {
      return false;
    }
    return true;
  };

  const getCroppedImg = async () => {
    const { x, y, width, height } = cropPixels;
    const { canvasWidth, canvasHeight } = getMaxImgSize({ width, height });

    return await imageCropper({
      width: canvasWidth,
      height: canvasHeight,
      img: originalImageElem,
      sx: x,
      sy: y,
      swidth: width,
      sheight: height,
      imageQuality: resultImageQuality,
      filename: "outfit",
      extension: "jpg"
    });
  };

  const displayNotice = type => {
    let content;
    switch (type) {
      case "tooSmallRawImage":
        content = <TXT name="msg_imageTooSmall" />;
        break;
      case "tooSmallCroppedImage":
        content = <TXT name="msg_croppedImgTooSmall" />;
        break;
      case "typeError":
        content = <TXT name="msg_wrongImgFormat" />;
        break;
      default:
        console.log("error");
        console.error("error");
        break;
    }
    NoticeModalActions.openModal({ content });
  };

  const handleLoadRawImage = async event => {
    setIsLoading(true);

    const { imgElem, dataUrl, err, errObj } = await ImageLoader.getImageElement(
      event
    );

    if (err) {
      if (err === "unknown" || err === "typeError") {
        displayNotice("typeError");
        if (errObj) AppErrorActions.setSafeAppError(errObj);
      }
      setIsLoading(false);
      return null;
    }

    const isGood = isSizeOfImageEnough(imgElem);

    if (isGood) {
      setCropData({
        originalImageElem: imgElem,
        originalImageDataURL: dataUrl,
        cropPixels: { x: null, y: null, width: null, height: null }
      });
    } else {
      displayNotice("tooSmallRawImage");
    }

    setIsLoading(false);
  };

  const handleImageCrop = cropPixels => {
    const { x, y, width, height } = cropPixels;
    setCropData({
      ...cropData,
      cropPixels: { x, y, width, height }
    });
  };

  const handleNextClick = async () => {
    if (!originalImageElem) return null;

    setIsLoading(true);

    const imageBlob = await getCroppedImg();
    onImageCrop(await blobToDataURL(imageBlob), cropData);

    setIsLoading(false);

    onNextClick();
  };

  return (
    <div>
      <LoadingModalView open={isLoading}></LoadingModalView>
      <ImageCropperPanelView
        originalImageElem={originalImageElem}
        originalImageDataURL={originalImageDataURL}
        cropPixels={cropPixels}
        imageRatio={getImageRatio(imageType)}
        minImgWidth={getMinImgWidth(imageType)}
        minImgHeight={getMinImgHeight(imageType)}
        onLoadRawImage={handleLoadRawImage}
        onCrop={handleImageCrop}
        onCancelClick={onCancelClick}
        onNextClick={handleNextClick}
        hasAddImageButtonClickedAtLeastOnce={
          hasAddImageButtonClickedAtLeastOnce
        }
        onAddImageBtnClick={onAddImageBtnClick}
      />
    </div>
  );
}

export default withStore({
  dispatchMap: ["noticeModal", "appError"]
})(ImageCropperPanel);
